"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.getVariantPricingBands = void 0;
const dayjs = require("dayjs");
const roundTo_1 = require("../roundTo");
const getLatestOrderLineVersion_1 = require("./getLatestOrderLineVersion");
const getVariantTotal_1 = require("./getVariantTotal");
const hotel_1 = require("../hotel");
const getVariantPricingBands = (variant) => {
    if (!variant)
        return null;
    if (!variant.order_lines)
        return null;
    let discounts = variant.order_lines.map((order_line) => {
        const discounts = getOrderLinePricingBands(order_line);
        if (!discounts)
            return null;
        return discounts;
    });
    // remove null values
    discounts = discounts.filter((discount) => discount);
    //remove the duplicates that exist in the discounts array.  In each array item check the nested array for duplicates based on check_in_date
    discounts = discounts.map((discount) => {
        return discount.filter((v, i, a) => a.findIndex((t) => t.check_in_date === v.check_in_date) === i);
    });
    if (!discounts || discounts.length === 0)
        return [];
    const dates = calculateEarlyBirdDates(discounts.flatMap((discount) => discount.map((d) => d)));
    const firstDate = get_first_date(variant);
    dates.push(firstDate);
    const prices = dates
        .map((date) => {
        return {
            date,
            total: (0, getVariantTotal_1.getVariantTotal)(variant, date),
        };
    })
        .sort((a, b) => {
        return dayjs(a.date).diff(dayjs(b.date));
    });
    return transformPriceData(removeDuplicateTotals(prices));
};
exports.getVariantPricingBands = getVariantPricingBands;
function removeDuplicateTotals(data) {
    const seen = new Set();
    return data.filter((item) => {
        if (!seen.has(item.total)) {
            seen.add(item.total);
            return true;
        }
        return false;
    });
}
const get_first_date = (variant) => {
    if (!variant)
        return "";
    const lines = variant?.order_lines || [];
    if (lines.length === 0)
        return "";
    const last_line = lines[0];
    if (!last_line.versions)
        return "";
    const last_version = (0, getLatestOrderLineVersion_1.getLatestOrderLineVersion)(last_line.versions);
    if (!last_version)
        return "";
    return dayjs(last_version.start_date).format("YYYY-MM-DD");
};
// Function to calculate the array of dates
const calculateEarlyBirdDates = (bookings) => {
    const result = [];
    bookings.forEach((booking) => {
        const checkInDate = dayjs(booking.check_in_date);
        if (booking) {
            booking?.early_bird?.forEach((earlyBird) => {
                const earlyBirdDate = checkInDate.subtract(earlyBird.min_days_in_advance, "day");
                result.push(earlyBirdDate.format("YYYY-MM-DD"));
            });
        }
    });
    return result;
};
const getOrderLinePricingBands = (item) => {
    let discounts = null;
    if (!item.versions)
        return discounts;
    const latest_version = (0, getLatestOrderLineVersion_1.getLatestOrderLineVersion)(item.versions);
    if (!latest_version)
        return discounts;
    if (latest_version.status.includes("CANCELLED"))
        return discounts;
    discounts = getOrderLinePricingBandsByType(latest_version.config, item.type);
    return discounts;
};
const getOrderLinePricingBandsByType = (config, type) => {
    let early_bird_discounts = [];
    switch (type) {
        case "HOTEL": {
            const hotel_config = config;
            const check_in_date = hotel_config?.where_when_who?.check_in_date;
            early_bird_discounts = hotel_config.rooms.rooms
                .map((room) => {
                if (room.applied_discount) {
                    const applied_discount = (0, hotel_1.getFilteredRoomDiscounts)(config?.date_of_confirmation, config?.where_when_who?.check_in_date, (0, hotel_1.getApplicableDiscountsForRatePolicies)(room.rate, config?.where_when_who?.number_of_nights, dayjs(config?.where_when_who?.check_in_date).diff(dayjs(), "day")));
                    if (!applied_discount)
                        return null;
                    if (applied_discount.best?.type === "long_stay")
                        return null;
                }
                if (!room.applied_discount?.early_bird)
                    return null;
                return {
                    check_in_date: check_in_date,
                    early_bird: room.applied_discount?.early_bird,
                };
            })
                .flatMap((discount) => discount);
            break;
        }
    }
    // Filter out any null values
    early_bird_discounts = early_bird_discounts?.filter((discount) => discount);
    // if it is an empty array, return null
    if (early_bird_discounts?.length === 0)
        return null;
    return early_bird_discounts;
};
function transformPriceData(data) {
    if (data.length === 0) {
        return [];
    }
    const finalPrice = data[data.length - 1].total;
    const transformed = [];
    for (let i = 0; i < data.length; i++) {
        let dates;
        const currentDate = new Date(data[i].date).toLocaleDateString("en-UK", {
            day: "numeric",
            month: "long",
            year: "numeric",
        });
        const discount = (0, roundTo_1.roundTo)(finalPrice - data[i].total, 2);
        if (i === 0) {
            dates = `Now until ${currentDate}`;
        }
        else {
            const previousDate = new Date(data[i - 1].date);
            previousDate.setDate(previousDate.getDate() + 1);
            const previousDateString = previousDate.toLocaleDateString("en-UK", {
                day: "numeric",
                month: "long",
                year: "numeric",
            });
            dates = `${previousDateString} to ${currentDate}`;
        }
        transformed.push({
            date: data[i].date,
            dates,
            total: data[i].total,
            discount,
        });
        // Break out of the loop after adding the last valid discount
        if (data.length > 1 && i === data.length - 2) {
            break;
        }
    }
    // Add the final entry with 'After ...' syntax
    if (data.length > 1) {
        transformed.push({
            date: data[data.length - 1].date,
            dates: `After ${new Date(data[data.length - 2].date).toLocaleDateString("en-UK", { day: "numeric", month: "long", year: "numeric" })}`,
            total: finalPrice,
            discount: 0,
        });
    }
    else {
        transformed.push({
            date: data[data.length - 1].date,
            dates: `After ${new Date(data[data.length - 1].date).toLocaleDateString("en-UK", { day: "numeric", month: "long", year: "numeric" })}`,
            total: finalPrice,
            discount: 0,
        });
    }
    return transformed;
}
